import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarCheck, Eye, FileText, Flame, IndianRupee, Lock, Mail, MessageCircle, Phone, PhoneCall, PhoneOutgoing, RotateCw, Send, Trophy, UserCheck, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Steps 0..count-1 round on a timer, paused while the tab is hidden.
 * For people who asked for reduced motion it never moves on its own and starts on the last step.
 * goTo jumps to a step and restarts the timer; `restart` changes on every jump, for restarting animations.
 */
export function useLoopingStep(count: number, intervalMs: number) {
  const [step, setStep] = useState(0);
  const [restart, setRestart] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) {
      if (restart === 0) setStep(count - 1);
      return;
    }
    const timer = setInterval(() => {
      if (!document.hidden) setStep((s) => (s + 1) % count);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [count, intervalMs, restart]);
  const goTo = useCallback((i: number) => {
    setStep(i);
    setRestart((r) => r + 1);
  }, []);
  return [step, goTo, restart] as const;
}

/**
 * Icons drifting around the browser window in the navy panel; each bobs on its own timing.
 * The ones along the bottom only show on tall screens, and the one beside the window only on very wide ones,
 * where there is room for them.
 */
const FLOATERS = [
  { icon: Phone, style: { top: "9%", right: "6%" }, size: "size-14", duration: "7s", delay: "0s" },
  { icon: Mail, style: { top: "27%", right: "5%" }, size: "size-11", duration: "8.5s", delay: "-2s" },
  { icon: FileText, style: { top: "50%", left: "1.5%" }, size: "size-11", duration: "9s", delay: "-1s", wide: true },
  { icon: MessageCircle, style: { top: "91%", left: "8%" }, size: "size-12", duration: "6.5s", delay: "-4s", tall: true },
  { icon: CalendarCheck, style: { top: "93%", left: "46%" }, size: "size-11", duration: "7.5s", delay: "-3s", tall: true },
  { icon: IndianRupee, style: { top: "90%", right: "8%" }, size: "size-14", duration: "8s", delay: "-5s", tall: true },
];

/** Decorative floating icons behind the navy panel's content; only on wide screens, still for reduced motion */
export function FloatingIcons() {
  return (
    <div aria-hidden="true" className="absolute inset-0 hidden xl:block pointer-events-none">
      {FLOATERS.map(({ icon: Icon, style, size, duration, delay, tall, wide }, i) => (
        <span
          key={i}
          className={cn(
            "auth-float absolute rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-sm items-center justify-center text-white/70 shadow-lg shadow-black/20",
            tall ? "hidden [@media(min-height:880px)]:flex" : wide ? "hidden 2xl:flex" : "flex",
            size,
          )}
          style={{ ...style, animationDuration: duration, animationDelay: delay }}
        >
          <Icon className="size-[42%]" />
        </span>
      ))}
    </div>
  );
}

const SCENE_MS = 2200;

/** Three parts of the CRM, each told in three short scenes; example data only */
const MODULES = [
  {
    title: "Leads",
    tab: "Leads",
    path: "leads",
    icon: UserPlus,
    scenes: [
      { icon: UserPlus, tone: "bg-[#E9EDFA] text-[#1E4FB4]", motion: "auth-pop", title: "New lead: Sri Lakshmi Mills", meta: "From IndiaMART · just now", badge: "New" },
      { icon: UserCheck, tone: "bg-[#E9EDFA] text-[#1E4FB4]", motion: "auth-pop", title: "Assigned to Priya", meta: "Owner picked by your lead rules" },
      { icon: Flame, tone: "bg-[#FFF3DE] text-[#8A4F00]", motion: "auth-pop", title: "Marked HOT", meta: "Decision maker · asked for a quote" },
    ],
  },
  {
    title: "Calls & WhatsApp",
    tab: "Calls",
    path: "calls",
    icon: Phone,
    scenes: [
      { icon: PhoneOutgoing, tone: "bg-[#EEF8F1] text-[#1F7A4D]", motion: "auth-ring", title: "Calling R. Venkatesh", meta: "Sri Lakshmi Mills · from the lead page" },
      { icon: PhoneCall, tone: "bg-[#EEF8F1] text-[#1F7A4D]", motion: "auth-pop", title: "Call logged", meta: "Follow-up set for tomorrow, 10:30" },
      { icon: MessageCircle, tone: "bg-[#EEF8F1] text-[#1F7A4D]", motion: "auth-pop", title: "WhatsApp sent", meta: "Catalogue and price list shared" },
    ],
  },
  {
    title: "Proposals & deals",
    tab: "Deals",
    path: "deals",
    icon: FileText,
    scenes: [
      { icon: Send, tone: "bg-[#E9EDFA] text-[#1E4FB4]", motion: "auth-pop", title: "Proposal sent", meta: "Shared with the customer as a link" },
      { icon: Eye, tone: "bg-[#FFF3DE] text-[#8A4F00]", motion: "auth-pop", title: "Proposal opened", meta: "Just now · reading the pricing page" },
      { icon: Trophy, tone: "bg-[#E3F4EA] text-[#1F7A4D]", motion: "auth-pop", title: "Deal moved to Won", meta: "Sri Lakshmi Mills · order created" },
    ],
  },
];

const SCENES_PER_MODULE = 3;
const SCENES = MODULES.flatMap((m) => m.scenes);

/**
 * Tour of the CRM drawn as a browser window, centred in the space under the headline: one browser tab per
 * module (Leads, Calls, Deals). The open tab's three activities appear one after another, then the next tab
 * takes over; clicking a tab jumps to it. Hidden on phones.
 */
export function AuthShowcase() {
  const [step, goTo, restart] = useLoopingStep(SCENES.length, SCENE_MS);
  const moduleIndex = Math.floor(step / SCENES_PER_MODULE);
  const sceneInModule = step % SCENES_PER_MODULE;
  const current = MODULES[moduleIndex] ?? MODULES[0]!;

  return (
    <div className="hidden lg:flex flex-1 items-center justify-center">
      {/* Drawn a little larger on very wide screens, where the panel has the room */}
      <div className="relative w-full max-w-[560px] 2xl:[zoom:1.15]">
        {/* A second window peeking out underneath, for depth */}
        <div className="absolute inset-x-6 -bottom-3.5 h-full rounded-2xl bg-white/10" />
        <div className="relative rounded-2xl overflow-hidden bg-white text-[#0E1033] shadow-2xl shadow-black/30">
          {/* Tab strip */}
          <div className="flex items-end gap-1 bg-[#E6E8EF] px-4 pt-3">
            <span className="flex items-center gap-1.5 mr-3 mb-3.5" aria-hidden="true">
              <span className="size-3 rounded-full bg-[#FF5F57]" />
              <span className="size-3 rounded-full bg-[#FEBC2E]" />
              <span className="size-3 rounded-full bg-[#28C840]" />
            </span>
            <div role="tablist" aria-label="What the CRM does" className="flex items-end gap-0.5 min-w-0">
              {MODULES.map((m, i) => {
                const active = i === moduleIndex;
                const TabIcon = m.icon;
                return (
                  <button
                    key={m.title}
                    type="button"
                    role="tab"
                    id={`auth-tab-${i}`}
                    aria-selected={active}
                    aria-controls="auth-tabpanel"
                    title={m.title}
                    onClick={() => goTo(i * SCENES_PER_MODULE + (prefersReducedMotion() ? SCENES_PER_MODULE - 1 : 0))}
                    className={cn(
                      "h-9 w-[124px] rounded-t-xl px-3.5 flex items-center gap-2 text-[13px] font-medium transition-colors duration-300",
                      "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#000080]",
                      active ? "bg-white text-[#0E1033]" : "text-[#5F6480] hover:bg-white/60",
                    )}
                  >
                    <TabIcon className={cn("size-4 shrink-0", active && "text-[#000080]")} aria-hidden="true" />
                    <span className="truncate">{m.tab}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Toolbar: back, forward, reload and the address of the open tab */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#ECEEF4] text-[#8A90AB]" aria-hidden="true">
            <ArrowLeft className="size-4" />
            <ArrowRight className="size-4" />
            <RotateCw className="size-4" />
            <span className="flex-1 min-w-0 h-8 rounded-full bg-[#F1F3F7] px-3.5 flex items-center gap-2 text-[13px] text-[#565B7A]">
              <Lock className="size-3.5 shrink-0" />
              <span className="truncate">
                sales.nelgates.com/<span key={moduleIndex} className="auth-fade inline-block text-[#0E1033]">{current.path}</span>
              </span>
            </span>
          </div>

          {/* Page-loading line: fills while this tab's three activities play */}
          <div className="h-0.5" aria-hidden="true">
            <span
              key={`${moduleIndex}-${restart}`}
              className="auth-progress block h-full bg-[#000080]/50"
              style={{ animationDuration: `${SCENE_MS * SCENES_PER_MODULE}ms` }}
            />
          </div>

          <div id="auth-tabpanel" role="tabpanel" aria-labelledby={`auth-tab-${moduleIndex}`} className="p-6">
            <div className="flex items-center justify-between pb-4 mb-3 border-b border-[#ECEEF4]">
              <span key={moduleIndex} className="auth-fade text-[11.5px] font-semibold tracking-[0.12em] uppercase text-[#5F6480]">
                Today’s activity · {current.title}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#1F7A4D]">
                <span className="size-1.5 rounded-full bg-[#1F7A4D] animate-pulse motion-reduce:animate-none" />
                Live
              </span>
            </div>
            {/* The open tab's activities arrive one by one; the newest is highlighted */}
            <ul key={moduleIndex} className="space-y-1.5">
              {current.scenes.map((s, i) => {
                const Icon = s.icon;
                const shown = i <= sceneInModule;
                const latest = i === sceneInModule;
                return (
                  <li
                    key={s.title}
                    aria-hidden={!shown}
                    className={cn(
                      "flex items-center gap-3.5 rounded-xl p-2.5 transition-all duration-500 ease-out",
                      shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3",
                      latest ? "bg-[#F4F5FA]" : "bg-transparent",
                    )}
                  >
                    <span key={latest ? step : "idle"} className={cn("size-11 shrink-0 rounded-xl flex items-center justify-center", s.tone, latest && s.motion)}>
                      <Icon className="size-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[15px] font-semibold">{s.title}</span>
                        {s.badge && <span className="shrink-0 rounded px-1.5 text-[10.5px] font-semibold leading-5 bg-[#E9EDFA] text-[#1E4FB4]">{s.badge}</span>}
                      </span>
                      <span className="block truncate text-[13px] text-[#565B7A]">{s.meta}</span>
                    </span>
                    {latest && <span className="shrink-0 text-[11px] font-medium text-[#5F6480]">now</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
